import type { Option } from "@tento-lona/sheets";
import type { GestureSystem } from "./gesture-system.ts";
import type { GlobalGestureStateManager } from "./global-gesture-state.ts";

/**
 * Tracks cross-system dismissal during pointer interactions.
 *
 * When another system has active state, this guard dismisses it.
 * Whether the current gesture is also blocked depends on the
 * `blocking` flag on the dismissed states and the `blockOnAnyDismissal`
 * option:
 *
 * - `blocking: true` (e.g., pending new event form): dismiss + block
 *   the current gesture. Prevents accidentally discarding a form.
 * - `blocking: false` (e.g., existing event details, context menu):
 *   dismiss + allow the current gesture to proceed by default.
 * - `blockOnAnyDismissal: true`: block even for non-blocking dismissals.
 *   Use this for empty-space gestures (ClickToCreate) where any dismissal
 *   should consume the interaction.
 *
 * Usage in gesture canHandle():
 * ```
 * if (this.guard.shouldBlock(isPointerDown)) return false;
 * // or for empty-space gestures:
 * if (this.guard.shouldBlock(isPointerDown, { blockOnAnyDismissal: true })) return false;
 * ```
 */
export class CrossSystemDismissalGuard {
  #dismissedBlockingThisInteraction = false;
  #dismissedAnyThisInteraction = false;
  #lastEventWasPointerUp = true;

  readonly #globalState: GlobalGestureStateManager;
  readonly #source: GestureSystem;
  constructor(
    source: GestureSystem,
    globalState: GlobalGestureStateManager,
  ) {
    this.#source = source;
    this.#globalState = globalState;
  }

  /**
   * Check if the gesture should be blocked due to cross-system dismissal.
   *
   * @param isPointerDown true if this is a pointer-down event (resets dismissal tracking)
   * @param options.blockOnAnyDismissal if true, block even for non-blocking dismissals
   * @returns true if the gesture should be blocked
   */
  shouldBlock(
    isPointerDown: boolean,
    options: Option<{ blockOnAnyDismissal?: boolean }>,
  ): boolean {
    // Reset the flags only at the START of a new pointer interaction.
    if (isPointerDown && this.#lastEventWasPointerUp) {
      this.#dismissedBlockingThisInteraction = false;
      this.#dismissedAnyThisInteraction = false;
    }
    this.#lastEventWasPointerUp = !isPointerDown;

    // If we already dismissed a blocking state in this interaction, keep blocking
    if (this.#dismissedBlockingThisInteraction) {
      return true;
    }

    // If we dismissed any state and caller blocks on any dismissal, keep blocking
    if (options?.blockOnAnyDismissal && this.#dismissedAnyThisInteraction) {
      return true;
    }

    // Check for states in other systems
    if (!this.#globalState.hasStateInOtherSystem(this.#source, null)) {
      return false;
    }

    // Check if any of those states are blocking
    const hasBlocking = this.#globalState.hasStateInOtherSystem(
      this.#source,
      (s) => s.blocking === true,
    );

    this.#globalState.dismissOtherSystems(this.#source, null);
    this.#dismissedAnyThisInteraction = true;

    if (hasBlocking) {
      this.#dismissedBlockingThisInteraction = true;
      return true;
    }

    // Non-blocking states dismissed
    if (options?.blockOnAnyDismissal) {
      return true;
    }

    // Default: allow gesture to proceed
    return false;
  }
}
